import { useQuery } from '@tanstack/react-query';
import { solutionStoriesApi, SolutionStoryOptionsGrouped } from '../../services/api';
import { SOLUTION_STORY_PRACTICE_OPTIONS } from '../customerSentiment/forms/solutionStoryPracticeOptions';
import { SOLUTION_STORY_INDUSTRY_OPTIONS } from '../customerSentiment/forms/solutionStoryIndustryOptions';
import type { IconMappings } from './SolutionStorySlidePreview';

export const SOLUTION_STORY_OPTIONS_QUERY_KEY = ['solution-story-options'] as const;

const EMPTY_ICON_MAPPINGS: IconMappings = {
  industry: {},
  industry_labels: {},
  practice: {},
  platform: {},
  techstack: {},
  company: {},
};

/** Fallback platform values (no bundled TS constant file exists for platforms). */
const FALLBACK_PLATFORM_OPTIONS = ['GCP', 'Google Maps', 'AWS', 'Google Workspace'];

/**
 * Fetch admin-configured practice/industry/platform options and icon mappings.
 * Falls back to the bundled TS constant lists while options load or on error,
 * so forms stay usable during rollout.
 */
export function useSolutionStoryOptions() {
  const query = useQuery({
    queryKey: SOLUTION_STORY_OPTIONS_QUERY_KEY,
    queryFn: () => solutionStoriesApi.getOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const data: SolutionStoryOptionsGrouped | undefined = query.data;

  const practiceOptions: string[] =
    data?.practice.map((o) => o.value) ?? [...SOLUTION_STORY_PRACTICE_OPTIONS];
  const industryOptions: string[] =
    data?.industry.map((o) => o.value) ?? [...SOLUTION_STORY_INDUSTRY_OPTIONS];
  const platformOptions: string[] =
    data?.platform.map((o) => o.value) ?? FALLBACK_PLATFORM_OPTIONS;
  const techStackOptions: string[] = data?.techstack?.map((o) => o.value) ?? [];
  const companyOptions: string[] = data?.company?.map((o) => o.value) ?? [];
  const iconMappings: IconMappings = (data?.icon_mappings as IconMappings) ?? EMPTY_ICON_MAPPINGS;

  return {
    ...query,
    practiceOptions,
    industryOptions,
    platformOptions,
    techStackOptions,
    companyOptions,
    iconMappings,
  };
}
